import { formatMinorUnitsInr } from '@/dashboard/billing/billing-entry-view';
/**
 * Money workspace - pure view logic (MONEY-DAY-CASH-TRUTH-V1; the founder
 * workspace ruling R1-R7 + the ENTERPRISE DESIGN CONSISTENCY LAW).
 *
 * No fetch, no React: the wire types, the summary-band composition, the
 * view/range/filter state resolution, and the labels. Money arrives in
 * INTEGER MINOR UNITS and formats ONLY through the shared billing
 * formatter (reused from money-desk-view) -- this module never re-derives
 * a money figure. The server's fold IS the truth; the band prints it.
 *
 * DESIGN CONSISTENCY: this module mints NO visual vocabulary. Tones are
 * the registered AcBadge / KpiTile tone tokens, nothing else.
 */

import { formatRs, istDateLabel, istTimeLabel } from './money-desk-view';

// ---- Wire types (mirror api/routes/accounts-workspace.ts) -----------------

export type MoneyViewToken =
  | 'day_ledger'
  | 'expenses'
  | 'deposits'
  | 'collections_by_mode';

export interface MoneyEnteredByWire {
  readonly label: string;
  readonly role: string;
}

export interface MoneyDayLedgerRowWire {
  readonly key: string;
  readonly businessDate: string;
  readonly atIso: string | null;
  readonly typeToken:
    | 'day_start'
    | 'collection'
    | 'expense'
    | 'petty_cash'
    | 'deposit'
    | 'reversal'
    | 'day_close';
  readonly typeLabel: string;
  readonly signedMinorUnits: number;
  readonly amountMinorUnits: number | null;
  readonly mode: string | null;
  readonly reference: string;
  readonly enteredBy: MoneyEnteredByWire | null;
  readonly proofState: 'attached' | 'pending' | 'missing' | null;
  readonly runningCashMinorUnits: number | null;
  readonly entryId: number | null;
  readonly paymentId: number | null;
}

export interface MoneyExpenseRowWire {
  readonly key: string;
  readonly entryId: number;
  readonly businessDate: string;
  readonly atIso: string;
  readonly category: string | null;
  readonly amountMinorUnits: number;
  readonly particulars: string;
  readonly paidTo: string | null;
  readonly remark: string | null;
  readonly proofState: 'attached' | 'pending' | 'missing';
  readonly proofReferenceNumber: string | null;
  readonly proofImageId: number | null;
  readonly enteredBy: MoneyEnteredByWire;
  readonly voidState: 'active' | 'reversed' | 'reversal';
  readonly reversesEntryId: number | null;
  readonly reversedByEntryId: number | null;
}

export interface MoneyDepositRowWire {
  readonly key: string;
  readonly entryId: number;
  readonly businessDate: string;
  readonly atIso: string;
  readonly amountMinorUnits: number;
  readonly bank: string | null;
  readonly reference: string | null;
  readonly remark: string;
  readonly proofState: 'attached' | 'pending' | 'missing';
  readonly proofImageId: number | null;
  readonly enteredBy: MoneyEnteredByWire;
  readonly voidState: 'active' | 'reversed' | 'reversal';
  readonly reversedByEntryId: number | null;
}

export interface MoneyCollectionRowWire {
  readonly key: string;
  readonly paymentId: number;
  readonly businessDate: string;
  readonly atIso: string;
  readonly amountMinorUnits: number;
  readonly method: string;
  readonly reachesDrawer: boolean;
  /** G1/G2: present ONLY when the viewer's role holds the configured
   *  name grant. Absent -- not null -- otherwise. */
  readonly patientLabel?: string | null;
}

export type MoneyWorkspaceRow =
  | MoneyDayLedgerRowWire
  | MoneyExpenseRowWire
  | MoneyDepositRowWire
  | MoneyCollectionRowWire;

export interface MoneySummaryWire {
  readonly currency: string;
  readonly collectedMinorUnits: number;
  readonly collectedByMode: readonly {
    readonly method: string;
    readonly transactionCount: number;
    readonly totalMinorUnits: number;
  }[];
  readonly collectionsCapped: boolean;
  readonly expensesMinorUnits: number;
  readonly depositsMinorUnits: number;
  readonly openingMinorUnits: number | null;
  readonly closingMinorUnits: number | null;
  readonly cashInHandMinorUnits: number | null;
  readonly countedAdjustmentMinorUnits: number;
  readonly postCloseAdjustmentMinorUnits?: number | null;
  readonly postCloseAdjustmentStatus?: 'measured' | 'not_closed' | 'incomplete_source' | 'unresolved_chain';
  readonly pendingCollections: { readonly state: 'not_derived' };
  readonly entriesCapped: boolean;
}

export interface MoneyViewDefWire {
  readonly token: MoneyViewToken;
  readonly label: string;
  readonly filters: readonly { readonly token: string; readonly label: string; readonly count?: number | null }[];
}

export interface MoneyWorkspaceWire {
  readonly clinicId: number;
  readonly range: {
    readonly from: string;
    readonly to: string;
    readonly dayCount: number;
    readonly isToday: boolean;
    /** The clinic's CURRENT IST business date, server-resolved. The
     *  client never computes "today" -- a browser in another timezone
     *  would pick a different day. */
    readonly today: string;
  };
  readonly view: MoneyViewToken;
  readonly filter: string;
  readonly views: readonly MoneyViewDefWire[];
  readonly summary: MoneySummaryWire;
  readonly rows: readonly MoneyWorkspaceRow[];
  readonly rowCount: number;
  readonly days: readonly {
    readonly businessDate: string;
    readonly openingMinorUnits: number | null;
    readonly closingMinorUnits: number | null;
    readonly derivedCloseMinorUnits: number | null;
    readonly countedCloseMinorUnits: number | null;
    readonly differenceMinorUnits: number | null;
    readonly acknowledged: boolean;
    readonly derivedAtCloseMinorUnits?: number | null;
    readonly postCloseAdjustmentMinorUnits?: number | null;
    readonly postCloseCorrected?: boolean;
    readonly postCloseStatus?: 'measured' | 'not_closed' | 'incomplete_source' | 'unresolved_chain';
    readonly cashInMinorUnits: number;
    readonly spentMinorUnits: number;
    readonly sentMinorUnits: number;
  }[];
  readonly chain: {
    readonly state: 'resolved' | 'pending_prior_day' | 'no_chain';
    readonly pendingDates: readonly string[];
    readonly carriedFromDate: string | null;
    readonly source: 'carryover' | 'first_count' | null;
  };
  readonly viewer: {
    readonly role: string;
    readonly canWrite: boolean;
    readonly canExport: boolean;
    readonly canSeeNames: boolean;
  };
  readonly asOf: string;
}

// ---- Labels ----------------------------------------------------------------

/** Payment-method display words. The tokens are the payments plane's own
 *  closed vocabulary; an unknown token renders humanised, never dropped
 *  (dropping it would make the by-mode rows disagree with the gross). */
export const PAYMENT_MODE_LABELS: Readonly<Record<string, string>> =
  Object.freeze({
    cash: 'Cash',
    upi: 'UPI',
    card: 'Card',
    insurance: 'Insurance',
    bank_transfer: 'Bank transfer',
    other: 'Other',
    unspecified: 'Mode not recorded',
  });

export function paymentModeLabel(method: string): string {
  return (
    PAYMENT_MODE_LABELS[method] ??
    method.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
  );
}

export const PROOF_LABELS: Readonly<Record<string, string>> = Object.freeze({
  attached: 'Voucher',
  pending: 'No photo yet',
  missing: 'No proof',
});

/** Proof chips use the REGISTERED AcBadge tones only -- no Money-local
 *  colour is minted (design consistency law rule 4). */
export function proofTone(
  state: 'attached' | 'pending' | 'missing',
): 'positive' | 'warning' | 'neutral' {
  if (state === 'attached') return 'positive';
  if (state === 'pending') return 'warning';
  return 'warning';
}

export const VOID_STATE_LABELS: Readonly<Record<string, string>> =
  Object.freeze({
    active: 'Recorded',
    reversed: 'Reversed',
    reversal: 'Reversal',
  });

// ---- Range presets (R3) ----------------------------------------------------

export type RangePreset = 'today' | 'yesterday' | 'last_7' | 'this_month' | 'custom';

export interface ResolvedRange {
  readonly from: string;
  readonly to: string;
}

/** Shift an IST business date label by whole days. Pure label arithmetic
 *  over UTC midnights -- a business date is a LABEL, not an instant, so
 *  this never crosses a timezone (the server owns instant conversion). */
export function shiftBusinessDate(date: string, days: number): string {
  const t = Date.parse(`${date}T00:00:00Z`);
  if (Number.isNaN(t)) return date;
  return new Date(t + days * 86400000).toISOString().slice(0, 10);
}

export function resolveRangePreset(
  preset: Exclude<RangePreset, 'custom'>,
  today: string,
): ResolvedRange {
  if (preset === 'today') return { from: today, to: today };
  if (preset === 'yesterday') {
    const y = shiftBusinessDate(today, -1);
    return { from: y, to: y };
  }
  if (preset === 'last_7') {
    return { from: shiftBusinessDate(today, -6), to: today };
  }
  return { from: `${today.slice(0, 7)}-01`, to: today };
}

export const RANGE_PRESET_LABELS: Readonly<Record<RangePreset, string>> =
  Object.freeze({
    today: 'Today',
    yesterday: 'Yesterday',
    last_7: 'Last 7 days',
    this_month: 'This month',
    custom: 'Custom range',
  });

/** Which preset (if any) a from/to pair corresponds to -- so the picker
 *  reflects a hand-typed range honestly instead of lying about "Today". */
export function presetForRange(
  range: ResolvedRange,
  today: string,
): RangePreset {
  for (const preset of ['today', 'yesterday', 'last_7', 'this_month'] as const) {
    const r = resolveRangePreset(preset, today);
    if (r.from === range.from && r.to === range.to) return preset;
  }
  return 'custom';
}

/** The range's own plain-language title (R3/R4: the band always says
 *  which days it is describing). */
export function rangeLabel(range: {
  readonly from: string;
  readonly to: string;
  readonly isToday: boolean;
}): string {
  if (range.isToday) return `Today - ${istDateLabel(range.from)}`;
  if (range.from === range.to) return istDateLabel(range.from);
  return `${istDateLabel(range.from)} to ${istDateLabel(range.to)}`;
}

// ---- The summary band (A2 / R4) -------------------------------------------

export interface SummaryTile {
  readonly key: string;
  readonly label: string;
  readonly value: string;
  readonly sub: string;
  readonly tone: 'ok' | 'warm' | 'revenue' | 'coming';
  readonly hero: boolean;
}

/** An unproven figure renders '-' with the truth named in `sub`, never a
 *  fake zero (the shell's DENSITY rule 6). */
const NOT_DERIVED = '-';

/**
 * TOLERANT MONEY READ (the walk24-2 crash class, inherited from the Q-02=A
 * recorded-collections strip law). A figure that is not a safe integer --
 * an absent field on a stale wire, a null, a NaN -- renders the honest dash.
 * It NEVER renders an invented Rs 0 and never reaches the formatter, which
 * would print "Rs NaN". Every money value in the band goes through here.
 */
export function tolerantRs(minorUnits: unknown): string {
  return typeof minorUnits === 'number' && Number.isSafeInteger(minorUnits)
    ? formatMinorUnitsInr(minorUnits, 2)
    : NOT_DERIVED;
}

/**
 * Compose the summary band. Collected and Cash in hand are DELIBERATELY
 * adjacent and DELIBERATELY labeled apart: the 2026-08-04 walk showed the
 * two being read as one number (M3), so each tile's sub line states what
 * moves it and what does not.
 */
export function composeSummaryTiles(input: {
  readonly summary: MoneySummaryWire;
  readonly range: MoneyWorkspaceWire['range'];
  readonly chainState: MoneyWorkspaceWire['chain']['state'];
}): readonly SummaryTile[] {
  const s = input.summary;
  const chainNote =
    input.chainState === 'no_chain'
      ? 'Record the first count to start the chain'
      : 'An earlier day is still open';
  const modeSummary =
    s.collectedByMode.length === 0
      ? 'No collections in this range'
      : s.collectedByMode
          .map((m) => `${paymentModeLabel(m.method)} ${tolerantRs(m.totalMinorUnits)}`)
          .join(' - ');

  return [
    {
      key: 'cash_in_hand',
      label: 'Cash in hand',
      value: tolerantRs(s.cashInHandMinorUnits),
      sub:
        s.cashInHandMinorUnits === null
          ? chainNote
          : `Till date, after expenses and deposits - ${
              input.range.from === input.range.to
                ? 'end of this day'
                : `end of ${istDateLabel(input.range.to)}`
            }${
              s.countedAdjustmentMinorUnits === 0
                ? ''
                : `. Includes ${formatRs(s.countedAdjustmentMinorUnits)} counted difference`
            }`,
      tone: 'revenue',
      hero: true,
    },
    {
      key: 'collected',
      label: 'Collected',
      value: tolerantRs(s.collectedMinorUnits),
      sub: s.collectionsCapped
        ? `Lower bound - large range. ${modeSummary}`
        : `Gross, every mode. Expenses never reduce this. ${modeSummary}`,
      tone: 'ok',
      hero: true,
    },
    {
      key: 'expenses',
      label: 'Expenses',
      value: tolerantRs(s.expensesMinorUnits),
      sub: 'Spent + petty cash, net of reversals. Reduces cash in hand only',
      tone: s.expensesMinorUnits > 0 ? 'warm' : 'ok',
      hero: false,
    },
    {
      key: 'deposits',
      label: 'Bank deposits',
      value: tolerantRs(s.depositsMinorUnits),
      sub: 'Moved to the bank. Reduces cash in hand, never collected',
      tone: 'ok',
      hero: false,
    },
    // C4: opening (range start) and cash in hand (the till-date position at
    // range end) are the two BALANCE facts, and they are labeled apart. There
    // is deliberately NO separate "Closing balance" tile: closing-of-range IS
    // cash in hand, and printing one number under two labels is exactly the
    // conflation the 2026-08-04 walk found (M3). The relationship is stated
    // in words on this tile instead.
    {
      key: 'opening',
      label: input.range.from === input.range.to ? 'Opening balance' : 'Opening of range',
      value: tolerantRs(s.openingMinorUnits),
      sub:
        s.openingMinorUnits === null
          ? chainNote
          : input.range.from === input.range.to
            ? 'Carried from the previous close - never typed. Cash in hand is where this day ends'
            : `Carried into ${istDateLabel(input.range.from)} - never typed. Cash in hand is where the range ends`,
      tone: 'ok',
      hero: false,
    },
    {
      // Key is the testid contract (`money-tile-${key}`): the visual-state
      // harness has pinned `money-tile-pending` since RECEPTION-OS-BUILD-V1.
      key: 'pending',
      label: 'Pending collections',
      value: NOT_DERIVED,
      sub: 'Not derived yet - due tracking rides the treatment-money package',
      tone: 'coming',
      hero: false,
    },
  ];
}

/**
 * The range identity, restated for the operator in one line so the band is
 * explainable IN PRODUCT without SQL (the evidence-rows law). Null when
 * the chain is unresolved -- there is no identity to state.
 */
export function rangeIdentityLine(
  summary: MoneySummaryWire,
): string | null {
  if (summary.openingMinorUnits === null || summary.closingMinorUnits === null) {
    return null;
  }
  const cashCollected =
    summary.collectedByMode.find((m) => m.method === 'cash')?.totalMinorUnits ?? 0;
  const parts = [
    `Opening ${formatRs(summary.openingMinorUnits)}`,
    `+ cash collected ${formatRs(cashCollected)}`,
    `- expenses ${formatRs(summary.expensesMinorUnits)}`,
    `- deposits ${formatRs(summary.depositsMinorUnits)}`,
  ];
  if (summary.countedAdjustmentMinorUnits !== 0) {
    parts.push(
      `+ counted difference ${formatRs(summary.countedAdjustmentMinorUnits)}`,
    );
  }
  if(summary.postCloseAdjustmentStatus==='measured'&&typeof summary.postCloseAdjustmentMinorUnits==='number'&&summary.postCloseAdjustmentMinorUnits!==0)parts.push(`− later collection correction (${signedRs(summary.postCloseAdjustmentMinorUnits)})`);
  parts.push(`= cash in hand ${formatRs(summary.closingMinorUnits)}`);
  return parts.join(' ');
}

/**
 * The band's own arithmetic check. The server already guarantees this
 * (one assembly feeds band, table and CSV), and the client asserts it
 * rather than trusting it -- a disagreement is a bug worth surfacing, not
 * a number worth printing.
 */
export function summaryIdentityHolds(summary: MoneySummaryWire): boolean {
  if (summary.openingMinorUnits === null || summary.closingMinorUnits === null) {
    return true;
  }
  const cashCollected =
    summary.collectedByMode.find((m) => m.method === 'cash')?.totalMinorUnits ?? 0;
  return (
    summary.openingMinorUnits +
      cashCollected -
      summary.expensesMinorUnits -
      summary.depositsMinorUnits +
      summary.countedAdjustmentMinorUnits - (summary.postCloseAdjustmentStatus==='measured' ? summary.postCloseAdjustmentMinorUnits??0 : 0) ===
    summary.closingMinorUnits
  );
}

// ---- Row helpers -----------------------------------------------------------

/** 'Thu 31 Jul, 2:40 PM' -- one time grammar for every view. */
export function rowWhen(businessDate: string, atIso: string | null): string {
  if (atIso === null) return istDateLabel(businessDate);
  return `${istDateLabel(businessDate)}, ${istTimeLabel(atIso)}`;
}

/** Signed money for a drawer-effect cell. Zero renders as an em-dash-free
 *  '0' with the reason on the row, never blank. */
export function signedRs(minorUnits: number): string {
  if (minorUnits === 0) return formatMinorUnitsInr(0, 2);
  return `${minorUnits > 0 ? '+' : '-'}${formatMinorUnitsInr(Math.abs(minorUnits), 2)}`;
}

/** The count line under every table ("14 rows - 3 days"). Always states
 *  the scope so a filtered table can never be mistaken for the whole. */
export function countLine(input: {
  readonly rowCount: number;
  readonly dayCount: number;
  readonly filter: string;
  readonly filterLabel: string;
  readonly capped: boolean;
}): string {
  const parts = [
    `${input.rowCount} ${input.rowCount === 1 ? 'row' : 'rows'}`,
    `${input.dayCount} ${input.dayCount === 1 ? 'day' : 'days'}`,
  ];
  if (input.filter !== 'all') parts.push(`filtered: ${input.filterLabel}`);
  if (input.capped) parts.push('scan bound hit - lower bound');
  return parts.join(' - ');
}

// ---- Deposit sheet validation (B/C3) --------------------------------------

export interface DepositFieldErrors {
  readonly amount?: string;
  readonly remark?: string;
  readonly proof?: string;
}

/** Mirrors the server writer's own rules so the error lands beside the
 *  field; the SERVER remains the fence (this is never the gate). */
export function validateDepositSheet(input: {
  readonly amountMinorUnits: number | null;
  readonly remark: string;
  readonly hasPhoto: boolean;
  readonly referenceNumber: string;
  readonly approverPicked: boolean;
  readonly approversAvailable: boolean;
}): DepositFieldErrors {
  const errors: Record<string, string> = {};
  if (input.amountMinorUnits === null) errors.amount = 'Enter the amount';
  if (input.remark.trim() === '') errors.remark = 'Say what this deposit was';
  if (
    !input.hasPhoto &&
    input.referenceNumber.trim() === '' &&
    !(input.approversAvailable && input.approverPicked)
  ) {
    errors.proof = input.approversAvailable
      ? 'Photograph the slip, enter the transaction reference, or pick the OK'
      : 'Photograph the slip or enter the transaction reference';
  }
  return errors;
}
